/**
 * Назначение: единые подписи и форматирование платежей в staff UI.
 * Где: реестр платежей (`/payments`) и история счетов (`/billing`).
 */

export type PaymentReferenceLike = {
  reference_type?: string | null;
  reference_id?: number | null;
};

export type PaymentStatusBadgeVariant = 'primary' | 'neutral' | 'warning' | 'danger';

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает',
  processing: 'В обработке',
  succeeded: 'Оплачен',
  partially_refunded: 'Частично возвращён',
  refunded: 'Возвращён',
  failed: 'Ошибка',
  cancelled: 'Отменён',
  expired: 'Истёк'
};

const PAYMENT_PROVIDER_LABELS: Record<string, string> = {
  yookassa: 'ЮKassa',
  robokassa: 'Robokassa',
  tochka: 'Точка',
  manual: 'Вручную',
  stub: 'Тестовый'
};

const amountFormatters = new Map<string, Intl.NumberFormat>();

function normalizeCurrency(currency: string | null | undefined): string {
  const cur = (currency ?? '').trim().toUpperCase();
  return cur || 'RUB';
}

function amountFormatter(currency: string): Intl.NumberFormat {
  const cached = amountFormatters.get(currency);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat('ru-RU', { style: 'currency', currency });
  amountFormatters.set(currency, formatter);
  return formatter;
}

/** Сумма из копеек: «2 000,00 ₽»; неизвестная валюта — число с кодом валюты. */
export function formatPaymentAmount(cents: number | null | undefined, currency?: string | null): string {
  const cur = normalizeCurrency(currency);
  const amount = (cents ?? 0) / 100;
  try {
    return amountFormatter(cur).format(amount);
  } catch {
    return `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${cur}`;
  }
}

/** Подпись статуса платежа; неизвестный код не показываем как есть. */
export function paymentStatusLabel(status: string | null | undefined): string {
  const key = (status ?? '').trim().toLowerCase();
  return PAYMENT_STATUS_LABELS[key] ?? 'Неизвестно';
}

/** Вариант бейджа для статуса платежа. */
export function paymentStatusBadgeVariant(status: string | null | undefined): PaymentStatusBadgeVariant {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'succeeded':
      return 'primary';
    case 'processing':
    case 'cancelled':
    case 'partially_refunded':
    case 'refunded':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Подпись провайдера платежа; неизвестный код оставляем как есть. */
export function paymentProviderLabel(provider: string | null | undefined): string {
  const raw = (provider ?? '').trim();
  if (!raw) return '—';
  return PAYMENT_PROVIDER_LABELS[raw.toLowerCase()] ?? raw;
}

/** Подпись типа связанной сущности. */
export function paymentReferenceTypeLabel(referenceType: string | null | undefined): string {
  const raw = (referenceType ?? '').trim();
  switch (raw.toLowerCase()) {
    case 'booking':
      return 'Запись';
    case 'subscription':
    case 'user_subscription':
      return 'Абонемент';
    case 'assistant_pack':
      return 'Пакет помощника';
    default:
      return raw.replaceAll('_', ' ');
  }
}

/** Понятная ссылка на связанную сущность: «Запись №99» вместо сырых типов. */
export function paymentReferenceLabel(reference: PaymentReferenceLike): string {
  const type = (reference.reference_type ?? '').trim();
  const id = reference.reference_id;
  const hasId = id != null && id > 0;
  if (type && hasId) return `${paymentReferenceTypeLabel(type)} №${id}`;
  if (type) return paymentReferenceTypeLabel(type);
  if (hasId) return `№${id}`;
  return '—';
}
